import React, { useEffect, useRef, useState } from 'react';
import blogAPI from '../../services/blogAPI';
import scheduledPostsAPI from '../../services/scheduledPostsAPI';
import './SchedulePostModal.css';

/**
 * Schedule Post Modal - Manual Post Creation
 * Supports creating multiple scheduled posts at once with accordion UI
 */
const SchedulePostModal = ({ isOpen, onClose, onSuccess }) => {
    const [creationMode, setCreationMode] = useState(null); // null, 'manual', or 'ai'
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [categories, setCategories] = useState([]);

    // Manual posts state
    const [manualPosts, setManualPosts] = useState([{
        id: '1',
        title: '',
        content: '',
        excerpt: '',
        category: '',
        tags: [],
        scheduledFor: '',
        scheduledTime: '09:00',
        featuredImage: {
            url: '',
            alt: '',
            caption: ''
        },
        seo: {
            title: '',
            description: '',
            keywords: []
        }
    }]);

    const [expandedPost, setExpandedPost] = useState('1');
    const [imageMode, setImageMode] = useState({ '1': 'upload' });
    const [newTagInput, setNewTagInput] = useState({});
    const [newKeywordInput, setNewKeywordInput] = useState({});
    const [uploadingImage, setUploadingImage] = useState({});
    const [generatingImage, setGeneratingImage] = useState({});
    const [imagePrompts, setImagePrompts] = useState({});
    const fileInputRefs = useRef({});

    // AI mode state
    const [aiConfig, setAiConfig] = useState({
        description: '',
        scheduleType: 'weekly', // 'weekly', 'biweekly', 'monthly', 'specific'
        dayOfWeek: 1, // Monday
        dayOfMonth: 1,
        time: '09:00',
        specificDates: [{ date: '', time: '09:00' }], // Array of {date, time} objects
        numberOfPosts: 4,
        tone: 'professional', // 'professional', 'casual', 'friendly', 'formal', 'creative', 'educational'
        length: 'medium', // 'short', 'medium', 'long'
        categories: [],
        customInstructions: '',
        includeImages: true,
        autoPublish: false
    });

    // Load categories when modal opens
    useEffect(() => {
        if (isOpen) {
            loadCategories();
            // Reset state
            setCreationMode(null);
            setManualPosts([{
                id: '1',
                title: '',
                content: '',
                excerpt: '',
                category: '',
                tags: [],
                scheduledFor: '',
                scheduledTime: '09:00',
                featuredImage: { url: '', alt: '', caption: '' },
                seo: { title: '', description: '', keywords: [] }
            }]);
            setExpandedPost('1');
            setError(null);
        }
    }, [isOpen]);

    const loadCategories = async () => {
        try {
            const response = await blogAPI.getAllCategories();
            setCategories(response.data.categories || []);
        } catch (err) {
            console.error('Error loading categories:', err);
        }
    };

    // AI Post Generation Function - Creates a recurring schedule
    const handleGenerateAIPosts = async () => {
        if (!aiConfig.description.trim()) {
            setError('Please describe what your posts should be about.');
            return;
        }

        // Validate specific dates
        if (aiConfig.scheduleType === 'specific') {
            const validDates = aiConfig.specificDates.filter(d => d.date && d.date.trim() !== '');
            if (validDates.length === 0) {
                setError('Please select at least one date for your posts.');
                return;
            }
        }

        setLoading(true);
        setError(null);

        try {
            console.log('📅 Creating AI post generation schedule...');
            console.log('AI Config:', aiConfig);

            // Create schedule configuration that will be stored and executed by backend
            const scheduleConfig = {
                // Schedule Details
                scheduleType: aiConfig.scheduleType, // 'weekly', 'biweekly', 'monthly', 'specific'
                time: aiConfig.time,

                // Content Configuration
                description: aiConfig.description,
                tone: aiConfig.tone,
                length: aiConfig.length,
                categories: aiConfig.categories,
                customInstructions: aiConfig.customInstructions,

                // Options
                includeImages: aiConfig.includeImages,
                autoPublish: aiConfig.autoPublish,

                // Metadata
                isActive: true,
                createdAt: new Date().toISOString()
            };

            // Add dayOfWeek, dayOfMonth, or specificDates based on schedule type
            if (aiConfig.scheduleType === 'monthly') {
                scheduleConfig.dayOfMonth = aiConfig.dayOfMonth;
            } else if (aiConfig.scheduleType === 'specific') {
                // Filter out empty dates and format them with their times
                scheduleConfig.specificDates = aiConfig.specificDates
                    .filter(d => d.date && d.date.trim() !== '')
                    .map(d => ({
                        date: d.date,
                        time: d.time
                    }))
                    .sort((a, b) => new Date(a.date) - new Date(b.date));

                console.log('🗓️ Specific dates being sent:', scheduleConfig.specificDates);
                console.log('🗓️ Original aiConfig.specificDates:', aiConfig.specificDates);
            } else {
                // For weekly and biweekly schedules
                scheduleConfig.dayOfWeek = aiConfig.dayOfWeek;
            }

            console.log('💾 Saving schedule configuration...');

            // Save the schedule configuration to backend
            // The backend will use a cron job to check this schedule and generate posts at the specified time
            const response = await scheduledPostsAPI.createAISchedule(scheduleConfig);

            console.log('✅ Schedule created successfully!');

            // Calculate monthly commitment or post count
            let postsPerMonth, scheduleDescription;

            if (aiConfig.scheduleType === 'specific') {
                const validDates = aiConfig.specificDates.filter(d => d.date && d.date.trim() !== '');
                postsPerMonth = validDates.length;
                const datesList = validDates.map(d => `${d.date} at ${d.time}`).join(', ');
                scheduleDescription = `on ${validDates.length} specific date${validDates.length > 1 ? 's' : ''} (${datesList})`;
            } else {
                postsPerMonth = aiConfig.scheduleType === 'weekly' ? 4
                    : aiConfig.scheduleType === 'biweekly' ? 2
                        : 1;

                scheduleDescription = aiConfig.scheduleType === 'weekly'
                    ? `every ${['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][aiConfig.dayOfWeek]} at ${aiConfig.time}`
                    : aiConfig.scheduleType === 'biweekly'
                        ? `every 2 weeks on ${['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][aiConfig.dayOfWeek]} at ${aiConfig.time}`
                        : `on day ${aiConfig.dayOfMonth} of each month at ${aiConfig.time}`;
            }

            const monthlyInfo = response.monthlyCommitment
                ? `\n\n📊 Monthly Commitment:\nUsing ${response.monthlyCommitment.new} of ${response.monthlyCommitment.limit} posts/month\nRemaining: ${response.monthlyCommitment.remaining} posts/month`
                : `\n\n📊 This will generate ${aiConfig.scheduleType === 'specific' ? postsPerMonth + ' posts' : '~' + postsPerMonth + ' posts per month'}`;

            alert(`✅ AI Post Schedule Created!\n\nPosts will be automatically generated ${scheduleDescription}${monthlyInfo}\n${aiConfig.includeImages ? '\n✅ With AI-generated images' : ''}\n${aiConfig.autoPublish ? '\n✅ Auto-publish enabled' : '\n📝 Posts will be saved as drafts'}`);

            // Close modal and refresh
            if (onSuccess) {
                onSuccess();
            }
            handleClose();

        } catch (err) {
            console.error('Error creating AI schedule:', err);
            const errorResponse = err.response?.data;

            // Show detailed quota error if available
            if (errorResponse && errorResponse.currentCommitment !== undefined) {
                setError(
                    `${errorResponse.message}\n\n` +
                    `Current monthly commitment: ${errorResponse.currentCommitment} posts\n` +
                    `This schedule requires: ${errorResponse.requestedPosts} posts/month\n` +
                    `Your monthly limit: ${errorResponse.limit} posts`
                );
            } else {
                setError(errorResponse?.message || err.message || 'Failed to create schedule. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    // Helper function to calculate scheduled dates
    const calculateScheduledDates = (scheduleType, dayOfWeek, dayOfMonth, numberOfPosts) => {
        const dates = [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (scheduleType === 'weekly') {
            let currentDate = new Date(today);
            // Find next occurrence of the day
            while (currentDate.getDay() !== dayOfWeek) {
                currentDate.setDate(currentDate.getDate() + 1);
            }

            for (let i = 0; i < numberOfPosts; i++) {
                dates.push(currentDate.toISOString().split('T')[0]);
                currentDate.setDate(currentDate.getDate() + 7);
            }
        } else if (scheduleType === 'biweekly') {
            let currentDate = new Date(today);
            // Find next occurrence of the day
            while (currentDate.getDay() !== dayOfWeek) {
                currentDate.setDate(currentDate.getDate() + 1);
            }

            for (let i = 0; i < numberOfPosts; i++) {
                dates.push(currentDate.toISOString().split('T')[0]);
                currentDate.setDate(currentDate.getDate() + 14);
            }
        } else if (scheduleType === 'monthly') {
            let currentDate = new Date(today);
            currentDate.setDate(dayOfMonth);

            // If the day has passed this month, move to next month
            if (currentDate < today) {
                currentDate.setMonth(currentDate.getMonth() + 1);
            }

            for (let i = 0; i < numberOfPosts; i++) {
                dates.push(currentDate.toISOString().split('T')[0]);
                currentDate.setMonth(currentDate.getMonth() + 1);
            }
        }

        return dates;
    };

    const addManualPost = () => {
        const newId = String(manualPosts.length + 1);
        setManualPosts([...manualPosts, {
            id: newId,
            title: '',
            content: '',
            excerpt: '',
            category: '',
            tags: [],
            scheduledFor: '',
            scheduledTime: '09:00',
            featuredImage: { url: '', alt: '', caption: '' },
            seo: { title: '', description: '', keywords: [] }
        }]);
        setExpandedPost(newId);
        setImageMode({ ...imageMode, [newId]: 'url' });
    };

    const removeManualPost = (id) => {
        if (manualPosts.length > 1) {
            setManualPosts(manualPosts.filter(p => p.id !== id));
            if (expandedPost === id) {
                setExpandedPost(manualPosts[0]?.id || null);
            }
        }
    };

    const updateManualPost = (id, field, value) => {
        setManualPosts(manualPosts.map(post =>
            post.id === id ? { ...post, [field]: value } : post
        ));
    };

    const updateManualPostSEO = (id, field, value) => {
        setManualPosts(manualPosts.map(post =>
            post.id === id
                ? { ...post, seo: { ...post.seo, [field]: value } }
                : post
        ));
    };

    const addTagToPost = (postId) => {
        const tagValue = newTagInput[postId]?.trim();
        if (!tagValue) return;

        const post = manualPosts.find(p => p.id === postId);
        if (post && !post.tags.includes(tagValue)) {
            updateManualPost(postId, 'tags', [...post.tags, tagValue]);
        }
        setNewTagInput({ ...newTagInput, [postId]: '' });
    };

    const removeTagFromPost = (postId, tag) => {
        const post = manualPosts.find(p => p.id === postId);
        if (post) {
            updateManualPost(postId, 'tags', post.tags.filter(t => t !== tag));
        }
    };

    const addKeywordToPost = (postId) => {
        const keywordValue = newKeywordInput[postId]?.trim();
        if (!keywordValue) return;

        const post = manualPosts.find(p => p.id === postId);
        if (post && !post.seo.keywords.includes(keywordValue)) {
            updateManualPostSEO(postId, 'keywords', [...post.seo.keywords, keywordValue]);
        }
        setNewKeywordInput({ ...newKeywordInput, [postId]: '' });
    };

    const removeKeywordFromPost = (postId, keyword) => {
        const post = manualPosts.find(p => p.id === postId);
        if (post) {
            updateManualPostSEO(postId, 'keywords', post.seo.keywords.filter(k => k !== keyword));
        }
    };

    const handleImageUpload = async (postId, event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            alert('Please select an image file');
            return;
        }

        // Validate file size (5MB max)
        if (file.size > 5 * 1024 * 1024) {
            alert('Image size must be less than 5MB');
            return;
        }

        try {
            setUploadingImage({ ...uploadingImage, [postId]: true });

            const formData = new FormData();
            formData.append('file', file);

            const response = await blogAPI.uploadMedia(formData);

            if (response.data && response.data.filePath) {
                updateManualPost(postId, 'featuredImage', {
                    url: response.data.filePath,
                    alt: file.name,
                    caption: ''
                });
                alert('Image uploaded successfully!');
            }
        } catch (err) {
            console.error('Error uploading image:', err);
            alert(err.response?.data?.message || 'Failed to upload image');
        } finally {
            setUploadingImage({ ...uploadingImage, [postId]: false });
            // Reset file input
            if (fileInputRefs.current[postId]) {
                fileInputRefs.current[postId].value = '';
            }
        }
    };

    const handleAIImageGeneration = async (postId) => {
        const prompt = imagePrompts[postId];
        if (!prompt || !prompt.trim()) {
            alert('Please enter an image description');
            return;
        }

        try {
            setGeneratingImage({ ...generatingImage, [postId]: true });

            const response = await blogAPI.generateImage({
                prompt: prompt.trim(),
                skipUsageTracking: false
            });

            if (response.data && response.data.imageUrl) {
                updateManualPost(postId, 'featuredImage', {
                    url: response.data.imageUrl,
                    alt: prompt,
                    caption: `AI generated: ${prompt}`
                });
                setImagePrompts({ ...imagePrompts, [postId]: '' });
                alert('Image generated successfully!');
            }
        } catch (err) {
            console.error('Error generating image:', err);
            const errorMsg = err.response?.data?.message || err.response?.data?.error || 'Failed to generate image';
            alert(errorMsg);
        } finally {
            setGeneratingImage({ ...generatingImage, [postId]: false });
        }
    };

    const validatePosts = () => {
        for (const post of manualPosts) {
            if (!post.title.trim()) {
                setError(`Post ${post.id}: Title is required`);
                return false;
            }
            if (!post.content.trim()) {
                setError(`Post ${post.id}: Content is required`);
                return false;
            }
            if (!post.scheduledFor) {
                setError(`Post ${post.id}: Scheduled date is required`);
                return false;
            }
        }
        return true;
    };

    const saveManualPosts = async () => {
        if (!validatePosts()) return;

        try {
            setLoading(true);
            setError(null);

            // Create scheduled posts
            for (const post of manualPosts) {
                const scheduledDateTime = new Date(`${post.scheduledFor}T${post.scheduledTime}`);

                const postData = {
                    title: post.title,
                    content: post.content,
                    excerpt: post.excerpt,
                    categories: post.category ? [post.category] : [],
                    tags: post.tags,
                    featuredImage: post.featuredImage.url ? post.featuredImage : undefined,
                    seo: {
                        metaTitle: post.seo.title,
                        metaDescription: post.seo.description,
                        keywords: post.seo.keywords
                    },
                    scheduling: {
                        type: 'once',
                        scheduledFor: scheduledDateTime.toISOString(),
                        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
                    },
                    autoPublish: true,
                    publishStatus: 'published'
                };

                await scheduledPostsAPI.createScheduledPost(postData);
            }

            alert(`Successfully scheduled ${manualPosts.length} post(s)!`);
            if (onSuccess) onSuccess();
            handleClose();
        } catch (err) {
            console.error('Error saving scheduled posts:', err);
            setError(err.message || 'Failed to schedule posts');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setCreationMode(null);
        setManualPosts([{
            id: '1',
            title: '',
            content: '',
            excerpt: '',
            category: '',
            tags: [],
            scheduledFor: '',
            scheduledTime: '09:00',
            featuredImage: { url: '', alt: '', caption: '' },
            seo: { title: '', description: '', keywords: [] }
        }]);
        setExpandedPost('1');
        setError(null);
        onClose();
    };

    const togglePost = (id) => {
        setExpandedPost(expandedPost === id ? null : id);
    };

    const getPostStatus = (post) => {
        if (post.scheduledFor) {
            return `Scheduled for ${new Date(post.scheduledFor + 'T' + post.scheduledTime).toLocaleString()}`;
        }
        return 'Not scheduled';
    };

    if (!isOpen) return null;

    // Mode selection screen
    if (!creationMode) {
        return (
            <div className="schedule-modal-overlay">
                <div className="schedule-modal mode-selection">
                    <div className="schedule-modal-header">
                        <h2>Schedule Blog Post</h2>
                        <button className="close-button" onClick={handleClose}>×</button>
                    </div>

                    <div className="schedule-modal-body">
                        <h3>Choose how you want to create your scheduled posts</h3>
                        <div className="creation-mode-options">
                            <button
                                className="mode-option-btn"
                                onClick={() => setCreationMode('manual')}
                            >
                                <h4>Manual Post Creation</h4>
                                <p>Write posts yourself and schedule them for publication</p>
                            </button>

                            <button
                                className="mode-option-btn"
                                onClick={() => setCreationMode('ai')}
                            >
                                <h4>AI-Powered Scheduling</h4>
                                <p>Let AI generate content automatically on a schedule</p>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Manual Post Creation Mode
    if (creationMode === 'manual') {
        return (
            <div className="schedule-modal-overlay">
                <div className="schedule-modal manual-creation">
                    <div className="schedule-modal-header">
                        <div className="header-left">
                            <button className="back-button" onClick={() => setCreationMode(null)}>
                                ← Back
                            </button>
                            <h2>Manual Post Creation</h2>
                        </div>
                        <button className="close-button" onClick={handleClose}>×</button>
                    </div>

                    <div className="schedule-modal-body">
                        {error && (
                            <div className="error-banner">
                                ⚠️ {error}
                            </div>
                        )}

                        {/* Posts Accordion */}
                        <div className="posts-accordion">
                            {manualPosts.map((post, index) => (
                                <div key={post.id} className="post-accordion-item">
                                    <div className="post-accordion-header" onClick={() => togglePost(post.id)}>
                                        <div className="post-number-badge">{index + 1}</div>
                                        <div className="post-info">
                                            <h4>Post {index + 1}</h4>
                                            <span className="post-status">{getPostStatus(post)}</span>
                                        </div>
                                        <div className="post-actions">
                                            {manualPosts.length > 1 && (
                                                <button
                                                    className="icon-button delete"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        removeManualPost(post.id);
                                                    }}
                                                >
                                                    🗑️
                                                </button>
                                            )}
                                            <button className="icon-button chevron">
                                                {expandedPost === post.id ? '▲' : '▼'}
                                            </button>
                                        </div>
                                    </div>

                                    {expandedPost === post.id && (
                                        <div className="post-accordion-content">
                                            {/* Post Title */}
                                            <div className="form-group">
                                                <label>Post Title *</label>
                                                <input
                                                    type="text"
                                                    value={post.title}
                                                    onChange={(e) => updateManualPost(post.id, 'title', e.target.value)}
                                                    placeholder="Enter post title..."
                                                />
                                            </div>

                                            {/* Content */}
                                            <div className="form-group">
                                                <label>Content *</label>
                                                <textarea
                                                    value={post.content}
                                                    onChange={(e) => updateManualPost(post.id, 'content', e.target.value)}
                                                    placeholder="Write your post content here... (supports Markdown)"
                                                    rows={10}
                                                />
                                            </div>

                                            {/* Excerpt */}
                                            <div className="form-group">
                                                <label>Excerpt</label>
                                                <textarea
                                                    value={post.excerpt}
                                                    onChange={(e) => updateManualPost(post.id, 'excerpt', e.target.value)}
                                                    placeholder="Brief summary..."
                                                    rows={3}
                                                />
                                            </div>

                                            {/* Category */}
                                            <div className="form-group">
                                                <label>Category</label>
                                                <select
                                                    value={post.category}
                                                    onChange={(e) => updateManualPost(post.id, 'category', e.target.value)}
                                                >
                                                    <option value="">Select category...</option>
                                                    {categories.map(cat => (
                                                        <option key={cat._id} value={cat._id}>{cat.name}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            {/* Featured Image */}
                                            <div className="form-group">
                                                <label>Featured Image</label>
                                                <div className="image-mode-tabs">
                                                    <button
                                                        type="button"
                                                        className={`image-tab-btn ${imageMode[post.id] === 'upload' ? 'active' : ''}`}
                                                        onClick={() => setImageMode({ ...imageMode, [post.id]: 'upload' })}
                                                    >
                                                        <span className="icon">📤</span>
                                                        Upload
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`image-tab-btn ${imageMode[post.id] === 'library' ? 'active' : ''}`}
                                                        onClick={() => setImageMode({ ...imageMode, [post.id]: 'library' })}
                                                    >
                                                        <span className="icon">📁</span>
                                                        Library
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`image-tab-btn ${imageMode[post.id] === 'ai' ? 'active' : ''}`}
                                                        onClick={() => setImageMode({ ...imageMode, [post.id]: 'ai' })}
                                                    >
                                                        <span className="icon">✨</span>
                                                        AI Generate
                                                    </button>
                                                </div>

                                                {/* Upload Mode */}
                                                {imageMode[post.id] === 'upload' && (
                                                    <div className="upload-container">
                                                        <input
                                                            ref={el => fileInputRefs.current[post.id] = el}
                                                            type="file"
                                                            accept="image/*"
                                                            onChange={(e) => handleImageUpload(post.id, e)}
                                                            style={{ display: 'none' }}
                                                        />
                                                        <button
                                                            type="button"
                                                            className="upload-btn"
                                                            onClick={() => fileInputRefs.current[post.id]?.click()}
                                                            disabled={uploadingImage[post.id]}
                                                        >
                                                            {uploadingImage[post.id] ? (
                                                                <>⏳ Uploading...</>
                                                            ) : (
                                                                <>📤 Choose File</>
                                                            )}
                                                        </button>
                                                        {post.featuredImage?.url && (
                                                            <div className="image-preview">
                                                                <img src={post.featuredImage.url} alt="Preview" />
                                                                <button
                                                                    type="button"
                                                                    className="remove-image-btn"
                                                                    onClick={() => updateManualPost(post.id, 'featuredImage', { url: '', alt: '', caption: '' })}
                                                                >
                                                                    ×
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {/* Library Mode */}
                                                {imageMode[post.id] === 'library' && (
                                                    <div className="image-library-placeholder">
                                                        <p>Media library coming soon</p>
                                                    </div>
                                                )}

                                                {/* AI Generate Mode */}
                                                {imageMode[post.id] === 'ai' && (
                                                    <div className="ai-image-container">
                                                        <div className="ai-prompt-group">
                                                            <input
                                                                type="text"
                                                                value={imagePrompts[post.id] || ''}
                                                                onChange={(e) => setImagePrompts({ ...imagePrompts, [post.id]: e.target.value })}
                                                                placeholder="Describe the image you want to generate..."
                                                                className="ai-prompt-input"
                                                            />
                                                            <button
                                                                type="button"
                                                                className="generate-btn"
                                                                onClick={() => handleAIImageGeneration(post.id)}
                                                                disabled={generatingImage[post.id] || !imagePrompts[post.id]?.trim()}
                                                            >
                                                                {generatingImage[post.id] ? (
                                                                    <>⏳ Generating...</>
                                                                ) : (
                                                                    <>✨ Generate</>
                                                                )}
                                                            </button>
                                                        </div>
                                                        {post.featuredImage?.url && (
                                                            <div className="image-preview">
                                                                <img src={post.featuredImage.url} alt="Preview" />
                                                                <button
                                                                    type="button"
                                                                    className="remove-image-btn"
                                                                    onClick={() => updateManualPost(post.id, 'featuredImage', { url: '', alt: '', caption: '' })}
                                                                >
                                                                    ×
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Scheduled Date & Time */}
                                            <div className="form-row">
                                                <div className="form-group">
                                                    <label>Scheduled Date *</label>
                                                    <input
                                                        type="date"
                                                        value={post.scheduledFor}
                                                        onChange={(e) => updateManualPost(post.id, 'scheduledFor', e.target.value)}
                                                        min={new Date().toISOString().split('T')[0]}
                                                    />
                                                </div>
                                                <div className="form-group">
                                                    <label>Scheduled Time *</label>
                                                    <input
                                                        type="time"
                                                        value={post.scheduledTime}
                                                        onChange={(e) => updateManualPost(post.id, 'scheduledTime', e.target.value)}
                                                    />
                                                </div>
                                            </div>

                                            {/* Tags */}
                                            <div className="form-group">
                                                <label>Tags</label>
                                                <div className="tags-container">
                                                    {post.tags.map(tag => (
                                                        <span key={tag} className="tag-pill">
                                                            {tag}
                                                            <button type="button" onClick={() => removeTagFromPost(post.id, tag)}>×</button>
                                                        </span>
                                                    ))}
                                                    <div className="tag-input-group">
                                                        <input
                                                            type="text"
                                                            value={newTagInput[post.id] || ''}
                                                            onChange={(e) => setNewTagInput({ ...newTagInput, [post.id]: e.target.value })}
                                                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTagToPost(post.id))}
                                                            placeholder="Add tag..."
                                                        />
                                                        <button type="button" onClick={() => addTagToPost(post.id)}>+</button>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* SEO Settings */}
                                            <div className="seo-settings">
                                                <h4>SEO Settings</h4>

                                                <div className="form-group">
                                                    <label>SEO Title</label>
                                                    <input
                                                        type="text"
                                                        value={post.seo.title}
                                                        onChange={(e) => updateManualPostSEO(post.id, 'title', e.target.value)}
                                                        placeholder="SEO optimized title..."
                                                    />
                                                </div>

                                                <div className="form-group">
                                                    <label>Meta description for search engines...</label>
                                                    <textarea
                                                        value={post.seo.description}
                                                        onChange={(e) => updateManualPostSEO(post.id, 'description', e.target.value)}
                                                        placeholder="Meta description..."
                                                        rows={3}
                                                    />
                                                </div>

                                                <div className="form-group">
                                                    <label>SEO Keywords</label>
                                                    <div className="tags-container">
                                                        {post.seo.keywords.map(keyword => (
                                                            <span key={keyword} className="tag-pill">
                                                                {keyword}
                                                                <button type="button" onClick={() => removeKeywordFromPost(post.id, keyword)}>×</button>
                                                            </span>
                                                        ))}
                                                        <div className="tag-input-group">
                                                            <input
                                                                type="text"
                                                                value={newKeywordInput[post.id] || ''}
                                                                onChange={(e) => setNewKeywordInput({ ...newKeywordInput, [post.id]: e.target.value })}
                                                                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addKeywordToPost(post.id))}
                                                                placeholder="Add SEO keyword..."
                                                            />
                                                            <button type="button" onClick={() => addKeywordToPost(post.id)}>+</button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* Add Another Post */}
                        <button className="add-post-button" onClick={addManualPost}>
                            + Add Another Post
                        </button>

                        {/* Footer Actions */}
                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={handleClose}>
                                ← Back
                            </button>
                            <button
                                className="btn-primary"
                                onClick={saveManualPosts}
                                disabled={loading}
                            >
                                Schedule {manualPosts.length} Post{manualPosts.length > 1 ? 's' : ''}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // AI Mode - AI-Powered Post Generation
    return (
        <div className="schedule-modal-overlay">
            <div className="schedule-modal ai-generation">
                <div className="schedule-modal-header">
                    <div className="header-left">
                        <button className="back-button" onClick={() => setCreationMode(null)}>
                            ← Back
                        </button>
                        <h2>AI-Powered Post Generation</h2>
                    </div>
                    <button className="close-button" onClick={handleClose}>×</button>
                </div>

                <div className="schedule-modal-body">
                    {error && (
                        <div className="error-banner">
                            ⚠️ {error}
                        </div>
                    )}

                    {/* What should your posts be about? */}
                    <div className="form-group">
                        <label>What should your posts be about? *</label>
                        <textarea
                            value={aiConfig.description}
                            onChange={(e) => setAiConfig({ ...aiConfig, description: e.target.value })}
                            placeholder="Describe the topic, theme, or subject matter for your blog posts..."
                            rows={4}
                        />
                        <small className="examples">
                            Examples:
                            <br />• Sustainable packaging solutions and eco-friendly business practices
                            <br />• Digital marketing tips for small businesses
                            <br />• Healthy recipes and nutrition advice for busy professionals
                        </small>
                    </div>

                    {/* How often should posts be published? */}
                    <div className="form-group">
                        <label>How often should posts be published? *</label>
                        <div className="schedule-frequency-grid">
                            <button
                                className={`frequency-option ${aiConfig.scheduleType === 'weekly' ? 'selected' : ''}`}
                                onClick={() => setAiConfig({ ...aiConfig, scheduleType: 'weekly' })}
                            >
                                <div className="frequency-title">Weekly</div>
                                <div className="frequency-subtitle">Every week</div>
                                <div className="frequency-commitment">~4 posts/month</div>
                            </button>
                            <button
                                className={`frequency-option ${aiConfig.scheduleType === 'biweekly' ? 'selected' : ''}`}
                                onClick={() => setAiConfig({ ...aiConfig, scheduleType: 'biweekly' })}
                            >
                                <div className="frequency-title">Bi-weekly</div>
                                <div className="frequency-subtitle">Every 2 weeks</div>
                                <div className="frequency-commitment">~2 posts/month</div>
                            </button>
                            <button
                                className={`frequency-option ${aiConfig.scheduleType === 'monthly' ? 'selected' : ''}`}
                                onClick={() => setAiConfig({ ...aiConfig, scheduleType: 'monthly' })}
                            >
                                <div className="frequency-title">Monthly</div>
                                <div className="frequency-subtitle">Every month</div>
                                <div className="frequency-commitment">1 post/month</div>
                            </button>
                            <button
                                className={`frequency-option ${aiConfig.scheduleType === 'specific' ? 'selected' : ''}`}
                                onClick={() => setAiConfig({ ...aiConfig, scheduleType: 'specific' })}
                            >
                                <div className="frequency-title">Specific Dates</div>
                                <div className="frequency-subtitle">Custom dates</div>
                            </button>
                        </div>
                    </div>

                    {/* Day of Week (for weekly/biweekly) */}
                    {(aiConfig.scheduleType === 'weekly' || aiConfig.scheduleType === 'biweekly') && (
                        <div className="form-group">
                            <select
                                value={aiConfig.dayOfWeek}
                                onChange={(e) => setAiConfig({ ...aiConfig, dayOfWeek: parseInt(e.target.value) })}
                                className="full-width-select"
                            >
                                <option value={1}>Monday</option>
                                <option value={2}>Tuesday</option>
                                <option value={3}>Wednesday</option>
                                <option value={4}>Thursday</option>
                                <option value={5}>Friday</option>
                                <option value={6}>Saturday</option>
                                <option value={0}>Sunday</option>
                            </select>
                        </div>
                    )}

                    {/* Day of Month (for monthly) */}
                    {aiConfig.scheduleType === 'monthly' && (
                        <div className="form-group">
                            <select
                                value={aiConfig.dayOfMonth}
                                onChange={(e) => setAiConfig({ ...aiConfig, dayOfMonth: parseInt(e.target.value) })}
                                className="full-width-select"
                            >
                                {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                                    <option key={day} value={day}>Day {day} of each month</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Specific Dates (for specific dates schedule) */}
                    {aiConfig.scheduleType === 'specific' && (
                        <div className="form-group">
                            <label>Select Dates *</label>
                            <div className="specific-dates-container">
                                {aiConfig.specificDates.map((dateObj, index) => (
                                    <div key={index} className="date-input-row">
                                        <input
                                            type="date"
                                            value={dateObj.date}
                                            onChange={(e) => {
                                                const newDates = [...aiConfig.specificDates];
                                                newDates[index] = { ...newDates[index], date: e.target.value };
                                                setAiConfig({ ...aiConfig, specificDates: newDates });
                                            }}
                                            min={new Date().toISOString().split('T')[0]}
                                            className="date-input"
                                        />
                                        <input
                                            type="time"
                                            value={dateObj.time}
                                            onChange={(e) => {
                                                const newDates = [...aiConfig.specificDates];
                                                newDates[index] = { ...newDates[index], time: e.target.value };
                                                setAiConfig({ ...aiConfig, specificDates: newDates });
                                            }}
                                            className="time-input"
                                        />
                                        {aiConfig.specificDates.length > 1 && (
                                            <button
                                                type="button"
                                                className="remove-date-btn"
                                                onClick={() => {
                                                    const newDates = aiConfig.specificDates.filter((_, i) => i !== index);
                                                    setAiConfig({ ...aiConfig, specificDates: newDates });
                                                }}
                                            >
                                                ×
                                            </button>
                                        )}
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    className="add-date-btn"
                                    onClick={() => {
                                        setAiConfig({
                                            ...aiConfig,
                                            specificDates: [...aiConfig.specificDates, { date: '', time: '09:00' }]
                                        });
                                    }}
                                >
                                    + Add Another Date
                                </button>
                            </div>
                            <small className="hint-text">
                                Note: Specific dates will generate posts manually at the selected times. They won't recur automatically.
                            </small>
                        </div>
                    )}

                    {/* Publishing Time & Number of Posts */}
                    {aiConfig.scheduleType !== 'specific' && (
                        <div className="form-row">
                            <div className="form-group">
                                <label className="label-gray">Publishing Time</label>
                                <input
                                    type="time"
                                    value={aiConfig.time}
                                    onChange={(e) => setAiConfig({ ...aiConfig, time: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label className="label-gray">Number of Posts to Generate</label>
                                <input
                                    type="number"
                                    min="1"
                                    max="20"
                                    value={aiConfig.numberOfPosts}
                                    onChange={(e) => setAiConfig({ ...aiConfig, numberOfPosts: parseInt(e.target.value) || 1 })}
                                />
                            </div>
                        </div>
                    )}

                    {/* Tone & Length */}
                    <div className="form-row">
                        <div className="form-group">
                            <select
                                value={aiConfig.tone}
                                onChange={(e) => setAiConfig({ ...aiConfig, tone: e.target.value })}
                                className="tone-select"
                            >
                                <option value="professional">Professional</option>
                                <option value="casual">Casual</option>
                                <option value="friendly">Friendly</option>
                                <option value="formal">Formal</option>
                                <option value="creative">Creative</option>
                                <option value="educational">Educational</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <select
                                value={aiConfig.length}
                                onChange={(e) => setAiConfig({ ...aiConfig, length: e.target.value })}
                                className="length-select"
                            >
                                <option value="short">Short (400-600 words)</option>
                                <option value="medium">Medium (800-1200 words)</option>
                                <option value="long">Long (1500-2000 words)</option>
                            </select>
                        </div>
                    </div>

                    {/* Categories (Optional) */}
                    <div className="form-group">
                        <label>Categories (Optional)</label>
                        <div className="checkbox-list">
                            {categories.map(cat => (
                                <label key={cat._id} className="checkbox-item">
                                    <input
                                        type="checkbox"
                                        checked={aiConfig.categories.includes(cat._id)}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setAiConfig({
                                                    ...aiConfig,
                                                    categories: [...aiConfig.categories, cat._id]
                                                });
                                            } else {
                                                setAiConfig({
                                                    ...aiConfig,
                                                    categories: aiConfig.categories.filter(id => id !== cat._id)
                                                });
                                            }
                                        }}
                                    />
                                    <span>{cat.name}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Additional Instructions (Optional) */}
                    <div className="form-group">
                        <label>Additional Instructions (Optional)</label>
                        <textarea
                            value={aiConfig.customInstructions}
                            onChange={(e) => setAiConfig({ ...aiConfig, customInstructions: e.target.value })}
                            placeholder="Any specific requirements or style preferences..."
                            rows={4}
                        />
                        <small className="examples">
                            Examples:
                            <br />• Include statistics and data points
                            <br />• Write in a storytelling format
                            <br />• Target small business owners
                            <br />• Focus on actionable tips
                        </small>
                    </div>

                    {/* Include AI-Generated Images */}
                    <div className="form-group">
                        <label className="checkbox-item-large">
                            <input
                                type="checkbox"
                                checked={aiConfig.includeImages}
                                onChange={(e) => setAiConfig({ ...aiConfig, includeImages: e.target.checked })}
                            />
                            <div className="checkbox-content">
                                <div className="checkbox-title">Include AI-Generated Images</div>
                                <div className="checkbox-subtitle">Generate unique featured images for each post</div>
                            </div>
                        </label>
                    </div>

                    {/* Auto-Publish Posts */}
                    <div className="form-group">
                        <label className="checkbox-item-large">
                            <input
                                type="checkbox"
                                checked={aiConfig.autoPublish}
                                onChange={(e) => setAiConfig({ ...aiConfig, autoPublish: e.target.checked })}
                            />
                            <div className="checkbox-content">
                                <div className="checkbox-title">Auto-Publish Posts</div>
                                <div className="checkbox-subtitle">Automatically publish posts on scheduled dates (otherwise saved as drafts)</div>
                            </div>
                        </label>
                    </div>

                    {/* Footer Actions */}
                    <div className="modal-footer">
                        <button className="btn-secondary" onClick={() => setCreationMode(null)}>
                            ← Back
                        </button>
                        <button
                            className="btn-primary btn-generate"
                            onClick={handleGenerateAIPosts}
                            disabled={loading || !aiConfig.description.trim()}
                        >
                            {loading ? (
                                <>⏳ Generating Posts...</>
                            ) : (
                                <>✨ Generate Posts</>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SchedulePostModal;
